
import React, { useState } from 'react';
// Added Check to the imports from lucide-react
import { Plus, Users, Calendar, Play, MoreVertical, Radio, X, ClipboardList, Trash2, Edit2, Clock, Send, AlertTriangle, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Meeting, User, UserRole } from '../types';

const Dashboard: React.FC<{ user: User }> = ({ user }) => {
  const [meetings, setMeetings] = useState<Meeting[]>(() => {
    const saved = localStorage.getItem('smsa_meetings');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [voterCount] = useState(() => {
    const saved = localStorage.getItem('smsa_delegates');
    return saved ? JSON.parse(saved).length : 0;
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastStatus, setBroadcastStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    startTime: '',
    endTime: ''
  });
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);

  const canCreateMeetings = [UserRole.SYSTEM_ADMIN, UserRole.PRESIDENT, UserRole.NATIONAL_OFFICER, UserRole.LOCAL_DIRECTOR].includes(user.role);

  const saveMeetings = (updated: Meeting[]) => {
    setMeetings(updated);
    localStorage.setItem('smsa_meetings', JSON.stringify(updated));
  };

  const createMeeting = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    
    if (editingMeeting) {
      const updated = meetings.map(m => m.id === editingMeeting.id ? { 
        ...m, 
        name: formData.name,
        startTime: formData.startTime,
        endTime: formData.endTime
      } : m);
      saveMeetings(updated);
    } else {
      const newMeeting: Meeting = {
        id: 'm' + Date.now(),
        name: formData.name,
        date: formData.startTime ? new Date(formData.startTime).toLocaleDateString() : new Date().toLocaleDateString(),
        startTime: formData.startTime,
        endTime: formData.endTime,
        status: 'UPCOMING',
        motions: []
      };
      saveMeetings([newMeeting, ...meetings]);
    }
    closeModal();
  };

  const deleteMeeting = (id: string) => {
    if (window.confirm("Delete this assembly permanently?")) {
      saveMeetings(meetings.filter(m => m.id !== id));
      setActiveMenuId(null);
    }
  };

  const openEdit = (m: Meeting) => {
    setEditingMeeting(m);
    setFormData({
      name: m.name,
      startTime: m.startTime || '',
      endTime: m.endTime || ''
    });
    setIsModalOpen(true);
    setActiveMenuId(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingMeeting(null);
    setFormData({ name: '', startTime: '', endTime: '' });
  };

  const handleBroadcast = (e: React.FormEvent) => {
    e.preventDefault();
    if (!broadcastMsg) return;
    setBroadcastStatus('sending');
    setTimeout(() => {
      setBroadcastStatus('sent');
      setTimeout(() => {
        setIsBroadcastOpen(false);
        setBroadcastStatus('idle');
        setBroadcastMsg('');
      }, 2000);
    }, 1500);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="relative flex h-3 w-3">
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">System Operational</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome, {user.name.split(' ')[0]}</h1>
          <p className="text-slate-500 font-medium tracking-tight">Accessing as <span className="text-indigo-600 font-bold">{user.role.replace('_', ' ')}</span></p>
        </div>
        
        {canCreateMeetings && (
          <div className="flex gap-3">
            <button 
              onClick={() => setIsBroadcastOpen(true)}
              className="bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl flex items-center gap-2 font-bold hover:bg-slate-50 transition-all text-sm"
            >
              <Radio size={18} className="text-slate-400" />
              Emergency Broadcast
            </button>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-indigo-100 text-sm"
            >
              <Plus size={20} />
              New Assembly
            </button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatsCard label="Active Sessions" value={meetings.filter(m => m.status === 'ONGOING').length.toString()} color="bg-indigo-500" />
        <StatsCard label="Registered Voters" value={voterCount.toString()} color="bg-emerald-500" />
        <StatsCard label="Portal Node" value="Production" color="bg-amber-500" />
      </div>

      <div className="grid grid-cols-1 gap-8">
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-800">Current Assemblies</h2>
          </div>

          {meetings.filter(m => m.status !== 'ARCHIVED').length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {meetings.filter(m => m.status !== 'ARCHIVED').map((meeting) => (
                <MeetingCard 
                  key={meeting.id} 
                  meeting={meeting} 
                  userRole={user.role} 
                  onDelete={deleteMeeting} 
                  onEdit={openEdit}
                  activeMenuId={activeMenuId}
                  setActiveMenuId={setActiveMenuId}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-3xl p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-slate-50 p-6 rounded-2xl mb-4 text-slate-300">
                <ClipboardList size={48} />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1">No Assemblies Initialized</h3>
              <p className="text-slate-500 max-w-sm mb-6 font-medium">There are currently no scheduled meetings. Use the action button above to start a session.</p>
              {canCreateMeetings && (
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2.5 rounded-xl font-bold transition-all text-sm flex items-center gap-2"
                >
                  <Plus size={18} />
                  Start First Session
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="font-black text-slate-800 uppercase tracking-tight">{editingMeeting ? 'Rename Assembly' : 'Initialize Assembly'}</h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={createMeeting} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Meeting Identifier</label>
                <input 
                  autoFocus
                  required
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Policy Workshop 2024" 
                  className="w-full px-4 py-4 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl transition-all outline-none font-bold text-slate-800"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Opening Date/Time</label>
                  <input 
                    type="datetime-local" 
                    value={formData.startTime}
                    onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl transition-all outline-none font-bold text-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Closing Date/Time</label>
                  <input 
                    type="datetime-local" 
                    value={formData.endTime}
                    onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl transition-all outline-none font-bold text-slate-800"
                  />
                </div>
              </div>

              <button type="submit" className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                {editingMeeting ? 'Update Record' : 'Confirm & Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {isBroadcastOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-rose-50">
              <div className="flex items-center gap-2 text-rose-600">
                <AlertTriangle size={20} />
                <h3 className="font-black uppercase tracking-tight">Emergency Broadcast</h3>
              </div>
              <button onClick={() => setIsBroadcastOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleBroadcast} className="p-8 space-y-6">
              <p className="text-xs font-bold text-slate-500">This message will be transmitted instantly to all active member sessions across the organization.</p>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Broadcast Message</label>
                <textarea 
                  required
                  autoFocus
                  value={broadcastMsg}
                  onChange={(e) => setBroadcastMsg(e.target.value)}
                  placeholder="Enter high-priority announcement..." 
                  className="w-full px-4 py-4 bg-slate-100 border-2 border-transparent focus:border-rose-500 focus:bg-white rounded-2xl transition-all outline-none font-bold text-slate-800 h-32"
                />
              </div>
              <button 
                disabled={broadcastStatus !== 'idle'}
                type="submit" 
                className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 ${
                  broadcastStatus === 'sent' ? 'bg-emerald-500 text-white' : 'bg-rose-600 text-white hover:bg-rose-700 shadow-rose-100'
                }`}
              >
                {broadcastStatus === 'idle' && <><Send size={18} /> Transmit Announcement</>}
                {broadcastStatus === 'sending' && <span className="animate-pulse">Broadcasting Node SY-V1...</span>}
                {broadcastStatus === 'sent' && <><Check size={18} /> Announcement Transmitted</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const StatsCard = ({ label, value, color }: { label: string, value: string, color: string }) => (
  <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col justify-between relative overflow-hidden group hover:border-indigo-200 transition-colors">
    <div className="flex justify-between items-start mb-2">
      <p className="text-slate-500 font-bold uppercase text-[10px] tracking-wider">{label}</p>
    </div>
    <div className="flex items-end justify-between">
      <p className={`text-3xl font-black text-slate-900`}>{value}</p>
    </div>
    <div className={`absolute bottom-0 left-0 h-1 transition-all duration-500 ${color} w-0 group-hover:w-full`}></div>
  </div>
);

const MeetingCard: React.FC<{ 
  meeting: Meeting, 
  userRole: UserRole, 
  onDelete: (id: string) => void,
  onEdit: (m: Meeting) => void,
  activeMenuId: string | null,
  setActiveMenuId: (id: string | null) => void
}> = ({ meeting, userRole, onDelete, onEdit, activeMenuId, setActiveMenuId }) => {
  const isAdmin = [UserRole.SYSTEM_ADMIN, UserRole.PRESIDENT, UserRole.LOCAL_DIRECTOR, UserRole.NATIONAL_OFFICER, UserRole.LOCAL_OFFICER].includes(userRole);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all group">
      <div className="p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className={`p-3 rounded-xl ${
            meeting.status === 'ONGOING' ? 'bg-emerald-50 text-emerald-600' :
            meeting.status === 'UPCOMING' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-50 text-slate-600'
          }`}>
            {meeting.status === 'ONGOING' ? <Play size={20} fill="currentColor" /> : <Calendar size={20} />}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="font-bold text-slate-900">{meeting.name}</h3>
              {meeting.status === 'ONGOING' && (
                <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-black uppercase animate-pulse">In Progress</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-400 font-bold uppercase tracking-tight">
              <span>{meeting.date}</span>
              {meeting.startTime && (
                <span className="flex items-center gap-1"><Clock size={12} /> {new Date(meeting.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              )}
              <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span className="flex items-center gap-1"><Users size={12} /> Registered Quorum</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3 relative">
          <Link 
            to={`/meeting/${meeting.id}`}
            className={`px-6 py-2.5 rounded-xl text-sm font-black uppercase tracking-widest transition-all ${
              meeting.status === 'ONGOING' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100 hover:bg-indigo-700' 
                : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}
          >
            {isAdmin ? 'Manage' : 'Cast Vote'}
          </Link>
          {isAdmin && (
            <div className="relative">
              <button 
                onClick={() => setActiveMenuId(activeMenuId === meeting.id ? null : meeting.id)}
                className="p-2.5 text-slate-400 hover:bg-slate-50 rounded-xl"
              >
                <MoreVertical size={18} />
              </button>
              {activeMenuId === meeting.id && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-slate-100 z-20 py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                   <button 
                     onClick={() => { onEdit(meeting); setActiveMenuId(null); }} 
                     className="w-full px-4 py-2 text-left text-xs font-bold text-slate-600 hover:bg-indigo-50 flex items-center gap-2"
                   >
                     <Edit2 size={14} /> Edit
                   </button>
                   <button 
                     onClick={() => { onDelete(meeting.id); setActiveMenuId(null); }} 
                     className="w-full px-4 py-2 text-left text-xs font-bold text-rose-500 hover:bg-rose-50 flex items-center gap-2"
                   >
                     <Trash2 size={14} /> Delete
                   </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
